<script setup lang="ts">
import type { IBadgeItems } from '@/shared/ui/Badge/types';
import Badge from '@/shared/ui/Badge/index.vue';
import { useModalsStore } from '@/app/store/modals';
import { PRODUCT_IMAGE_PLACEHOLDER_PATH } from '@/shared/constants/placeholders';
import type { IProduct } from '@/widgets/productCard/model/types/product';
import { MIN_BOUGHT_AMOUNT } from '@/shared/constants/badges';

const props = defineProps<{ product: IProduct }>();

const emit = defineEmits<{
  openModal: [index: number];
}>();

const { setModalState } = useModalsStore();
const { t } = useI18n();

const openModal = (index: number) => {
  emit('openModal', index);
  setModalState('product', true);
};

const badges: Ref<IBadgeItems[]> = ref([
  {
    id: 1,
    text: 'Ликвидация',
    type: 'liquidation',
    isShow: Boolean(props.product.liquidation),
  },
  {
    id: 2,
    text: String(props.product.bought_count),
    type: 'bought',
    isShow: Boolean(props.product.bought_count > MIN_BOUGHT_AMOUNT),
  },
  {
    id: 3,
    text: String(props.product.in_stock),
    type: 'remained',
    isShow: Boolean(props.product.in_stock),
  },
  {
    id: 4,
    text: 'Новинка',
    type: 'new_arrival',
    isShow: Boolean(props.product.new_arrival),
  },
]);

const images = computed(() => props.product.images || []);
const smallImages = computed(() => images.value.slice(1, 4));
</script>

<template>
  <div class="product-detail-images">
    <div class="product-detail-images__main">
      <ul class="product-detail-images__badges">
        <li
          v-for="badge in badges"
          :key="badge.id"
          v-show="badge.isShow"
          class="product-detail-images__badge"
        >
          <Badge :id="badge.id" :text="badge?.text" :type="badge?.type" />
        </li>
      </ul>
      <button
        class="product-detail-images__button"
        :aria-label="t('viewProductPhoto')"
        @click.prevent="openModal(0)"
      >
        <NuxtImg
          :src="images?.[0]?.image"
          loading="lazy"
          :placeholder="PRODUCT_IMAGE_PLACEHOLDER_PATH"
          alt="Фото товара"
          class="product-detail-images__image"
        />
      </button>
    </div>
    <div v-if="smallImages.length" class="product-detail-images__image-sidebar">
      <button
        v-for="(image, index) in smallImages"
        :key="image.id"
        class="product-detail-images__button"
        :aria-label="t('viewProductPhoto')"
        @click.prevent="openModal(index + 1)"
      >
        <NuxtImg
          :src="image?.image"
          loading="lazy"
          :placeholder="PRODUCT_IMAGE_PLACEHOLDER_PATH"
          alt="Фото товара"
          class="product-detail-images__image _small"
        />
      </button>
    </div>
  </div>
</template>

<style lang="less">
.product-detail-images {
  display: flex;
  gap: @size-x5;

  &__image {
    display: block;
    object-fit: cover;
    width: 100%;
    height: 314px;
    border-radius: @size-x4;

    &._small {
      width: 100px;
      height: 100px;
      border-radius: @size-x2;
    }
  }

  &__main {
    width: 100%;
  }

  &__image-sidebar {
    .flex-column(@size-x2);
  }

  &__badges {
    .flex-column(@main-size);

    position: absolute;
    left: @size-x5;
    bottom: @size-x5;
  }

  &__button {
    .button-reset();

    width: 100%;
  }

  @media @1024 {
    &__badges {
      gap: 2px;
      bottom: 6px;
      left: 6px;
    }
  }
}
</style>
